/**
 * 簡易予測の一括取込の確認ダイアログ (§6)。
 *
 * 簡易予測手法の選択後、実際の取込実行前に確認を挟む。
 * AC-035: メッセージは手法名 (methodName) のみで構成し、「予測エンジン」という
 * 表現を一切含まない。
 */
import { useTranslation } from 'react-i18next'

import {
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'

type Props = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  /**
   * 簡易予測の手法名 (例: 「単純前日コピー」)。
   */
  methodName?: string
  isLoading?: boolean
}

export const ImportConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  methodName,
  isLoading = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // 確定履歴の取込は確認を出さない (AC-DFD-031。取り消せる操作のため)。本ダイアログは
  // 簡易予測の一括取込専用で、手法名が決まるまで本文は空にする。
  const message =
    methodName !== undefined
      ? t('page:demandForecastDaily.simpleForecast.confirmMessage', {
          methodName,
        })
      : ''

  const footer = (
    <HStack gap={3}>
      <TertiaryButton disabled={isLoading} onClick={onClose}>
        {t('common:action.cancel')}
      </TertiaryButton>
      <PrimaryButton loading={isLoading} onClick={onConfirm}>
        {t('page:demandForecastDaily.confirmRecordImport.importButton')}
      </PrimaryButton>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      // 幅は UI 辞書 components.md `.ecl-modal` の 4 段 (既定 md)。 8080 に対応する画面が無いので既定の md
      size="md"
      title={t('page:demandForecastDaily.simpleForecast.confirmTitle')}
    >
      {/* ダイアログの主文。 8080 の確認モーダル本文は実測 14px /
          rgba(0,0,0,0.87) = Text.highEmphasis で、 以前の `fontSize="sm"`
          (= 12px) は 2 段小さかった (2026-08-28 デザイナー指摘「フォント m、
          primary」)。 同じ画面の `ConfirmDemandDialog` が 2026-08-26 の同種指摘で
          既に `textStyle="body"` (md / line-height 1.6) へ直しており、 その
          canonical role をそのまま使う。 生の fontSize 指定に戻さないこと。 */}
      <Text textStyle="body" color="Text.highEmphasis">
        {message}
      </Text>
    </Modal>
  )
}
